import * as S from './NavigationBar.styles';
import { Icon } from 'icon/Icon';
import { UserMenu } from '../UserMenu';
import { EuiIcon } from '@elastic/eui';

import { ROUTES, isMobileRoute } from 'enums';
import { useIntl } from 'react-intl';
import { RouteValue, RouteName } from 'types';

import { UserProfile } from '../../generated/graphql';
import { useContext, useEffect, useState } from 'react';
import { WhiteLabelContext } from 'providers/white-label/WhiteLabelProvider';
import { useIsMobile } from 'hooks/useIsMobile';

export interface NavigationBarProps {
  sideBarIsOpen: boolean;
  onToggleSideBar: () => void;
  routes: typeof ROUTES;
  activePath: RouteValue;
  onClickMenuItem: (route: RouteValue) => void;
  onClickUserProfile: () => void;
  userProfile?: Partial<UserProfile>;
}

export const NavigationBar = ({
  sideBarIsOpen,
  onToggleSideBar,
  routes,
  activePath,
  onClickMenuItem,
  onClickUserProfile,
  userProfile,
}: NavigationBarProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const { name: iconLogoName } = useContext(WhiteLabelContext);
  const isMobile = useIsMobile();
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  useEffect(() => setMenuIsOpen(false), [activePath, isMobile]);

  const routeNames = (Object.keys(routes) as RouteName[])
    .filter((route) => routes[route] !== ROUTES.login)
    .filter((route) => !isMobile || isMobileRoute(routes[route]));

  const showSidebarToggle = activePath.length > 1 && (!isMobile || isMobileRoute(activePath));

  return (
    <S.Container>
      {showSidebarToggle && (
        <S.SideBarButton
          aria-label={formatMessage({ id: 'mobile.sidebar.toggle' })}
          test-id="sidebar-toggle"
          isOpen={sideBarIsOpen}
          onClick={onToggleSideBar}
        >
          <Icon iconName="SideBarToggle" />
        </S.SideBarButton>
      )}
      <S.Logo>
        <Icon iconName={iconLogoName} width={isMobile ? '120' : '205'} />
      </S.Logo>
      {isMobile ? (
        <>
          <S.Burger
            test-id="mobile-menu-button"
            aria-label={formatMessage({ id: 'mobile.menu.open' })}
            aria-expanded={menuIsOpen}
            onClick={(): void => setMenuIsOpen((prev) => !prev)}
          >
            <EuiIcon key={menuIsOpen ? 'cross' : 'menu'} type={menuIsOpen ? 'cross' : 'menu'} color="ghost" size="l" />
          </S.Burger>
          {menuIsOpen && (
            <>
              <S.MobileBackdrop onClick={(): void => setMenuIsOpen(false)} />
              <S.MobilePanel test-id="mobile-menu">
                {routeNames.map((route) => (
                  <S.MobileMenuItem
                    test-id={`mobile-menu-item-${route}`}
                    selected={routes[route] === activePath}
                    key={route}
                    onClick={(): void => {
                      setMenuIsOpen(false);
                      onClickMenuItem(routes[route]);
                    }}
                  >
                    {formatMessage({ id: route })}
                  </S.MobileMenuItem>
                ))}
                <S.MobileUser
                  test-id="mobile-menu-profile"
                  onClick={(): void => {
                    setMenuIsOpen(false);
                    onClickUserProfile();
                  }}
                >
                  {formatMessage({ id: 'mobile.menu.profile' })}
                  <span>{`${userProfile?.firstName || '-'} ${userProfile?.lastName || '-'}`}</span>
                  {userProfile?.vendorName && <span>{userProfile.vendorName}</span>}
                </S.MobileUser>
              </S.MobilePanel>
            </>
          )}
        </>
      ) : (
        <>
          <S.Menu>
            {routeNames.map((route) => (
              <S.MenuItem
                selected={routes[route] === activePath}
                key={route}
                onClick={(): void => {
                  onClickMenuItem(routes[route]);
                }}
              >
                {formatMessage({ id: route })}
              </S.MenuItem>
            ))}
          </S.Menu>
          <S.User>
            <UserMenu onClick={onClickUserProfile} userProfile={userProfile} />
          </S.User>
        </>
      )}
    </S.Container>
  );
};
